<template>
	<div class="page-content-wrapper"
		 :style="{backgroundColor: backgroundColorStyle}"
		 :class="{'has-app-plain-footer': props.hasPlainFooter, 'has-app-bottom-bar': props.hasAppBottomBar, 'has-mobile-bottom-bar': !props.withoutMobileBottomBar && $device.isMobile}">
		<div id="page-content" class="page-content stretch-layout" :class="[paddingClass]">
			<slot/>
		</div>
	</div>
</template>

<script setup lang="ts">
import {computed} from 'vue';
import {useNuxtApp} from '#app';

const {$device} = useNuxtApp();

const props = defineProps<{
	// Whether a top spacing  shall be ignored
	full?: boolean,
	// Whether a top spacing on 'xs' devices shall be ignored
	xsFull?: boolean,
	// Whether a top spacing on 'sm' devices shall be ignored
	smFull?: boolean,
	// Whether a top spacing on 'md' devices shall be ignored
	mdFull?: boolean,
	// Whether a top spacing on 'lg' devices shall be ignored
	lgFull?: boolean,
	// Whether only a tight top spacing on mobile devices shall be added
	mobileTight?: boolean,
	// Whether the AppPlainFooter component exists within the page content
	// Adapts the margin so the footer is only visible when scrolled to it
	hasPlainFooter?: boolean,
	// Whether the AppBottomBar component exists within the page content
	// Adapts the padding to the bottom
	hasAppBottomBar?: boolean,
	// Whether the MobileBottomBar component does not exist within the page content on mobile devices which is assumed by default
	// Adapts the padding to the bottom
	withoutMobileBottomBar?: boolean,
	// The color of the background
	// If 'false' no background color is applied
	// If 'true' a light grey background is applied
	background?: string | boolean,
	// The color of the background on desktop devices
	// If 'false' no background color is applied
	// If 'true' a light grey background is applied
	backgroundDesktop?: string | boolean,
}>();

const backgroundColorStyle = computed(() => {
	return (props.background === true || props.background === '') || ($device.isDesktop && (props.backgroundDesktop === true || props.backgroundDesktop === '')) ? '#FBFBFB' : props.background;
});

const paddingClass = computed(() => {
	const padding = props.full ? [] : [props.mobileTight ? 'pt-16 sm:pt-24' : 'pt-24'];

	if (props.lgFull) {
		padding.push('lg:pt-0')
	}

	if (props.mdFull) {
		padding.push('md:pt-0')

		if (!props.lgFull) {
			padding.push('lg:pt-24')
		}
	}

	if (props.smFull) {
		padding.push('sm:pt-0')

		if (!props.mdFull) {
			padding.push('md:pt-24')
		}
	}

	if (props.xsFull) {
		padding.push('xs:pt-0')

		if (!props.smFull) {
			padding.push('sm:pt-24')
		}
	}

	return padding.join(' ')
});

</script>

<style>
/*********************
  Typography
**********************/
/*********************
  Inputs
**********************/
/*********************
  Shadows
**********************/
/*********************
  Layout
**********************/
/*********************
  Card
**********************/
/*********************
  Opacity
**********************/
.page-content-wrapper {
  min-height: 100vh;
  display: flex;
  position: relative;
  flex-direction: column;
}
.app-header + .app-content .page-content-wrapper, .app-header + div > .app-content .page-content-wrapper {
  box-sizing: border-box;
  margin-top: -56px;
  padding-top: 56px;
}

.app-header.is-hidden + .app-content .page-content-wrapper, .app-header.is-hidden + div > .app-content .page-content-wrapper {
  top: -56px;
}

.app-content .app-header-toolbar + .page-content-wrapper {
  top: 0;
  transition: top 0.3s ease 0s;
  padding-top: 56px;
}
@media (max-width: 767px) {
  .app-content .app-header-toolbar + .page-content-wrapper {
    padding-top: 56px;
  }
}

.app-content .app-header-toolbar.has-content + .page-content-wrapper {
  padding-top: 128px;
}
@media (max-width: 767px) {
  .app-content .app-header-toolbar.has-content + .page-content-wrapper {
    padding-top: 108px;
  }
}

.app-content .app-header-toolbar.has-content.has-tools + .page-content-wrapper {
  padding-top: 170px;
}
@media (max-width: 767px) {
  .app-content .app-header-toolbar.has-content.has-tools + .page-content-wrapper {
    padding-top: 150px;
  }
}

.app-content .app-header-toolbar.has-content.has-tabs + .page-content-wrapper {
  padding-top: 170px;
}
@media (max-width: 767px) {
  .app-content .app-header-toolbar.has-content.has-tabs + .page-content-wrapper {
    padding-top: 150px;
  }
}

.page-content-wrapper.has-mobile-bottom-bar.has-app-plain-footer {
  margin-bottom: 48px;
}
.page-content-wrapper.has-mobile-bottom-bar.has-app-plain-footer .page-content {
  padding-bottom: 8px;
}
.page-content-wrapper.has-mobile-bottom-bar:not(.has-app-plain-footer) .page-content {
  padding-bottom: 48px;
}

.page-content-wrapper.has-app-bottom-bar .page-content {
  padding-bottom: 88px;
}
@media (max-width: 767px) {
  .page-content-wrapper.has-app-bottom-bar .page-content {
    padding-bottom: 56px;
  }
}
.page-content-wrapper.has-app-bottom-bar.has-app-plain-footer {
  margin-bottom: 56px;
}
@media (max-width: 767px) {
  .page-content-wrapper.has-app-bottom-bar.has-app-plain-footer {
    margin-bottom: 56px;
  }
}
.page-content-wrapper.has-app-bottom-bar.has-app-plain-footer .page-content {
  padding-bottom: 42px;
}
@media (max-width: 767px) {
  .page-content-wrapper.has-app-bottom-bar.has-app-plain-footer .page-content {
    padding-bottom: 16px;
  }
}

.page-content-wrapper.has-mobile-bottom-bar.has-app-bottom-bar.has-app-plain-footer {
  margin-bottom: 104px;
}
.page-content-wrapper.has-mobile-bottom-bar.has-app-bottom-bar.has-app-plain-footer .page-content {
  padding-bottom: 24px;
}
.page-content-wrapper.has-mobile-bottom-bar.has-app-bottom-bar:not(.has-app-plain-footer) .page-content {
  padding-bottom: 104px;
}
</style>